import { FC, useContext } from 'react';
import { Upload, Typography } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { EmailCommunicationRecord } from '#types';
import { parseEmailFile, ParentUploadFunction } from '../parser/communicationParser';
import FlexBox from '../../../../atoms/FlexBox';
import { MessageContext } from '../../../../../providers/MessageContext';

const { Dragger } = Upload;
const { Text } = Typography;

interface EmailUploaderProps {
    disabled?: boolean;
    onEmailParsed: (email: EmailCommunicationRecord) => void;
    uploadAttachmentFunction: ParentUploadFunction;
    parentDocId?: string;
}

const EmailUploader: FC<EmailUploaderProps> = ({
    disabled = false,
    onEmailParsed,
    uploadAttachmentFunction,
    parentDocId,
}) => {
    const { messageApi } = useContext(MessageContext);

    /**
     * Handles email file upload by saving the original file as an attachment
     * and parsing its contents for the communication log
     */
    const handleFileDrop = async (file: File) => {
        try {
            if (!parentDocId) {
                messageApi.error({ content: 'Cannot import email: Parent document ID is missing.', key: 'emlUploadError', duration: 3 });
                console.error('EmailUploader: parentDocId is missing.');
                return Upload.LIST_IGNORE;
            }
            const lowerFileName = file.name.toLowerCase();
            if (lowerFileName.endsWith('.eml') || lowerFileName.endsWith('.msg')) {
                const emailDate = new Date();
                const timestampSuffix = emailDate.toISOString();
                const fileSuffix = file.name.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 50);
                const emailRecordId = `${parentDocId}#EMAIL#${timestampSuffix}_${fileSuffix}#${crypto.randomUUID()}`;
                
                try {
                    const rcFile = file as any;
                    rcFile.uid = rcFile.uid || crypto.randomUUID();
                    await uploadAttachmentFunction(rcFile, emailRecordId);
                    messageApi.success({ content: 'Email file saved as attachment', key: 'emailFileUploaded', duration: 2 });
                } catch (uploadError) {
                    console.error('EmailUploader: Failed to upload original email file', uploadError);
                    messageApi.warning({ content: 'Email file could not be saved as attachment, but will continue parsing', key: 'emailUploadWarning', duration: 3 });
                }
                
                const emailData = await parseEmailFile(
                    file,
                    uploadAttachmentFunction,
                    parentDocId,
                    emailRecordId
                );
                
                onEmailParsed(emailData);
                messageApi.success({ content: 'Email imported and added to Communication Log', key: 'emailUploadSuccess', duration: 2 });
                return false;
            }
            messageApi.error('Please upload an .eml or .msg file');
            return Upload.LIST_IGNORE;
        } catch (error) {
            console.error('Failed to parse email file:', error);
            messageApi.error({ content: `Failed to import ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`, key: 'emailUpload', duration: 3 });
            return Upload.LIST_IGNORE;
        }
    };

    return (
        <div
            style={{
                flex: 1,
                minWidth: 200,
            }}
            className='no-print'
        >
            <Dragger
                name="email"
                multiple={true}
                showUploadList={false}
                beforeUpload={handleFileDrop}
                accept=".eml,.msg"
                disabled={disabled}
                aria-label="Email file uploader"
            >
                <FlexBox
                    justifyCenter
                    wrap
                    style={{
                        margin: -12,
                    }}
                >
                    <FlexBox noGrow>
                        <MailOutlined style={{ fontSize: 20 }} />
                        <Text>
                            Click or drop .eml or .msg files here
                        </Text>
                    </FlexBox>
                </FlexBox>
            </Dragger>
        </div>
    );
};

export default EmailUploader;